import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import axios from "axios";
import API_BASE_URL from "../../../src/config";
import CropModal from "../../components/CropModal";

const PhotosAndVideos = forwardRef(({ propStayId }, ref) => {
  const stayId = propStayId || localStorage.getItem("stayId");

  const [coverPhoto, setCoverPhoto] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [galleryIds, setGalleryIds] = useState([]);

  const [loading, setLoading] = useState(true);

  // Crop queue system
  const [cropFile, setCropFile] = useState(null);
  const [cropQueue, setCropQueue] = useState([]);
  const [isCoverCrop, setIsCoverCrop] = useState(false);
  const [showCrop, setShowCrop] = useState(false);

  // ================= FETCH DATA =================
  useEffect(() => {
    const fetchStayDetails = async () => {
      if (!stayId) return;

      try {
        const token = localStorage.getItem("accessToken") || "";
        const res = await axios.post(
          `${API_BASE_URL}stays/details`,
          { id: stayId },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (res.data?.success && res.data?.data) {
          const stay = res.data.data;

          if (stay.cover_image) {
            setCoverPreview(stay.cover_image);
          }

          if (Array.isArray(stay.galleries)) {
            setGalleryPreviews(stay.galleries.map((g) => g.image_url));
            setGalleryIds(stay.galleries.map((g) => g.id));
          }
        }
      } catch (error) {
        console.error("Error fetching stay details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStayDetails();
  }, [stayId]);

  // ================= COVER =================
  const handleCoverPhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsCoverCrop(true);
    setCropFile(file);
    setShowCrop(true);
  };

  const handleCoverCropComplete = (blob) => {
    setCoverPhoto(blob);
    setCoverPreview(URL.createObjectURL(blob));
    setShowCrop(false); // close modal
  };

  // ================= GALLERY =================
  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setIsCoverCrop(false);

    //  set queue
    setCropQueue(files);

    // first image
    setCropFile(files[0]);
    setShowCrop(true);
  };

  const handleGalleryCropComplete = (blob) => {
    // save image
    setGalleryPhotos((prev) => [...prev, blob]);
    setGalleryPreviews((prev) => [...prev, URL.createObjectURL(blob)]);
    setGalleryIds((prev) => [...prev, null]);

    // move to next
    setCropQueue((prevQueue) => {
      const newQueue = prevQueue.slice(1);

      if (newQueue.length > 0) {
        setCropFile(newQueue[0]);
        setShowCrop(true);
      } else {
        setShowCrop(false); // last image → close
      }

      return newQueue;
    });
  };

  // ================= DELETE =================
  const handleDeleteGallery = async (index) => {
    const galleryId = galleryIds[index];

    if (!galleryId) {
      const newPreviews = [...galleryPreviews];
      const newIds = [...galleryIds];
      const newPhotos = [...galleryPhotos];

      newPreviews.splice(index, 1);
      newIds.splice(index, 1);
      newPhotos.splice(index, 1);

      setGalleryPreviews(newPreviews);
      setGalleryIds(newIds);
      setGalleryPhotos(newPhotos);
      return;
    }

    if (!window.confirm("Are you sure you want to delete this image?")) return;

    try {
      const token = localStorage.getItem("accessToken") || "";

      await axios.delete(`${API_BASE_URL}stays/delete-stay-photo`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { stayId, galleryId },
      });

      const newPrev = [...galleryPreviews];
      const newIds = [...galleryIds];

      newPrev.splice(index, 1);
      newIds.splice(index, 1);

      setGalleryPreviews(newPrev);
      setGalleryIds(newIds);
    } catch (err) {
      alert("Failed to delete image.");
    }
  };

  // ================= SUBMIT =================
  useImperativeHandle(ref, () => ({
    submit: async () => {
      if (!stayId) throw new Error("stayId missing");

      const formData = new FormData();
      formData.append("stayId", stayId);

      if (coverPhoto) formData.append("cover_image", coverPhoto);

      galleryPhotos.forEach((file) => {
        formData.append("gallery_images", file);
      });

      const token = localStorage.getItem("accessToken") || "";

      const res = await axios.post(`${API_BASE_URL}stays/upload-photos`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      return res.data;
    },
  }));

  if (loading) return <p>Loading...</p>;

  return (
    <div className="photos-section">

      {/* ================= CROP MODAL ================= */}
      {showCrop && (
        <CropModal
          file={cropFile}
          onClose={() => {
            setShowCrop(false);
            setCropQueue([]);
          }}
          onComplete={
            isCoverCrop ? handleCoverCropComplete : handleGalleryCropComplete
          }
        />
      )}

      {/* ================= COVER ================= */}
      <div className="cover-photo-card mb-5">
        <h4>Cover Photo</h4>
        <div className="coverphotobx">
          <img
            src={coverPreview || "https://placehold.co/800x400?text=No+Cover"}
            className="cover-photo"
          />
          <label className="cover-upload-btn">
            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
              <path d="M15 12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h1.172a3 3 0 0 0 2.12-.879l.83-.828A1 1 0 0 1 6.827 3h2.344a1 1 0 0 1 .707.293l.828.828A3 3 0 0 0 12.828 5H14a1 1 0 0 1 1 1zM2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828.828A2 2 0 0 1 3.172 4z"/>
              <path d="M8 11a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5m0 1a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M3 6.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0"/>
            </svg>
            <input type="file" accept="image/*" onChange={handleCoverPhotoChange} />
          </label>
        </div>
      </div>

      {/* ================= GALLERY ================= */}
      <div className="gallery-section">
        <h4>Photos assigned to spaces</h4>

        <div className="gallery-grid">

          {/* IMAGES FIRST */}
          {galleryPreviews.map((src, i) => (
            <div key={i} className="gallery-tile position-relative">
              <img src={src} alt="" />
              <button onClick={() => handleDeleteGallery(i)} className="delete-btn">×</button>
            </div>
          ))}

          {/* PLUS BUTTON LAST */}
          <label className="gallery-tile upload-tile">
            <span className="plus-icon">+</span>
            <input type="file" multiple accept="image/*" onChange={handleGalleryChange} />
          </label>

        </div>
      </div>
    </div>
  );
});

export default PhotosAndVideos;